import "@formatjs/intl-durationformat/polyfill.js";
import "temporal-polyfill/global";
import { PUBLIC_PTC_SERVER_URL } from "./env";
import { configureRestart } from "./lib/restart";
import { Router } from "./lib/router";
import type { QueryClient } from "@tanstack/query-core";
import { type AppRouter, createAppRouter, createQueryClient } from "./routes/app-router";
import { LocalStorage } from "./lib/storage";
import { initHelpscout } from "./vendor/helpscout";
import { initSentry } from "./vendor/sentry";

import { App } from "@pentacode/core";

/**
 * "standalone": the app owns the page (all current PWAs).
 * "embedded": the app is mounted into a partner host page via the integration
 * wrapper (PTC-3919) — no Sentry, no service worker, no full-page reloads.
 */
export type AppMode = "standalone" | "embedded";

export interface InitAppOptions {
    mode: AppMode;
    /**
     * Overrides the build-time `PUBLIC_PTC_SERVER_URL` so one published integration
     * artifact can serve different partners at runtime.
     */
    serverUrl?: string;
    /**
     * Embedded only: invoked when the app asks for a full restart (logout,
     * company switch, applied update). The wrapper answers with a
     * dispose/re-mount cycle; standalone mode reloads the page instead.
     * `route`, when given, is the route to re-mount at (e.g. the post-login
     * `next` target) instead of the current one.
     */
    onRestartRequest?: (route?: string) => void;
}

// Live bindings, assigned by initApp() from each entry point before the first
// custom element is constructed. Importers keep `import { app, router } from
// "../init"` unchanged, but must not dereference either at module-evaluation
// time (verified across all importers for PTC-3919).
export let app: App;
export let router: Router;
export let appRouter: AppRouter;
export let queryClient: QueryClient;
export let appMode: AppMode;

// Vite HMR re-runs a changed module's whole import graph (fresh `?t=` query),
// re-executing this module while the entry point — and thus initApp() — never
// runs again. A dev graph can also mix invalidated (`?t=`) and cached module
// URLs, which evaluates a second copy of this module *before* the entry point
// initializes. Both copies adopt the singletons — right away when they already
// exist, otherwise as soon as initApp() publishes them — so no importer is left
// with undefined bindings (see @pentacode/apps-common/vite/README.md).
// Dev-only: production runs each module exactly once.
if (import.meta.hot) {
    const adopt = () => {
        app = window.app!;
        router = window.router!;
        appRouter = window.appRouter!;
        queryClient = window.queryClient!;
        appMode = window.__ptcAppMode ?? "standalone";
    };

    (window.__ptcAppAdopters ??= new Set()).add(adopt);

    if (window.app && window.router) {
        adopt();
    }
}

export function initApp(options: InitAppOptions) {
    if (app) {
        // Already initialized — HMR adoption above or a repeated call.
        return;
    }

    appMode = options.mode;
    configureRestart({ embedded: appMode === "embedded", onRestartRequest: options.onRestartRequest });
    // TODO(PTC-4250): remove legacy migration in versions > 1.49.3
    app = new App(new LocalStorage(appMode === "standalone"), options.serverUrl ?? PUBLIC_PTC_SERVER_URL);
    router = new Router("/", { mode: options.mode === "embedded" ? "embedded" : "history" });
    queryClient = createQueryClient();
    appRouter = createAppRouter({ legacy: router, app, queryClient });
    // Cached time-entry-derived data (roster, time reports, balances) is out of date once writes reach the server.
    app.onTimeEntriesSynced(() => void queryClient.invalidateQueries({ refetchType: "none" }));

    if (import.meta.hot) {
        window.app = app;
        window.router = router;
        window.appRouter = appRouter;
        window.queryClient = queryClient;
        window.__ptcAppMode = appMode;
        window.__ptcAppAdopters?.forEach((adopt) => adopt());
    }

    // In embedded mode the host page owns error reporting, and third-party
    // UI must not be injected into the partner page (PTC-3919).
    if (appMode === "standalone") {
        void initSentry(app);
        initHelpscout();
    }
}

type AppDisposeCallback = () => void | Promise<void>;

// Modules holding app-lifetime state register a reset here at module
// evaluation. The registry is intentionally never cleared: modules evaluate
// once per page, while an embedded app can be disposed and re-initialized
// repeatedly — callbacks must cope with running once per dispose cycle.
const disposeCallbacks = new Set<AppDisposeCallback>();

export function onAppDispose(callback: AppDisposeCallback) {
    disposeCallbacks.add(callback);
}

/**
 * Counterpart to initApp() for the embedded integration (PTC-3919): runs the
 * registered teardown callbacks and drops the singletons so a subsequent
 * initApp() starts a fresh instance. Standalone entry points never call this —
 * there the page unload is the teardown.
 */
export async function disposeApp() {
    await Promise.all(
        [...disposeCallbacks].map(async (callback) => {
            try {
                await callback();
            } catch (e) {
                console.error("Error in app dispose callback:", e);
            }
        })
    );

    if (router) {
        router.destroy();
    }
    appRouter?.history.destroy();
    queryClient?.clear();

    app = undefined!;
    router = undefined!;
    appRouter = undefined!;
    queryClient = undefined!;
    appMode = undefined!;
    configureRestart({ embedded: false });

    if (import.meta.hot) {
        window.app = undefined;
        window.router = undefined;
        window.appRouter = undefined;
        window.queryClient = undefined;
        window.__ptcAppMode = undefined;
    }
}
